import React from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Home from './pages/Home'
import Login from './pages/Login'
import Campaigns from './pages/Campaigns'
import { CampaignDashboardPage } from './pages/dashboard/CampaignDashboardPage'
import { AdUnitDashboardPage } from './pages/dashboard/AdUnitDashboardPage'
import { setAuthReturnTo, currentPathForReturn } from './utils/authRedirect'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Preserve the full URL (pathname + search + hash) so login can
      // restore it. We stash it in BOTH sessionStorage (survives the
      // 401 full-page reload, though this branch is SPA-only) and
      // router `state.from` (the immediate SPA hand-off). The Login
      // page prefers state.from and falls back to sessionStorage.
      const from = currentPathForReturn(location)
      setAuthReturnTo(from)
      navigate('/login', { state: { from } })
    }
  }, [navigate, location, isAuthenticated, isLoading])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Home - New chat */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* Conversation detail - loads specific conversation */}
      <Route
        path="/c/:conversationId"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* Campaigns list - shows campaigns grid */}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* Ad unit editor (Creative Studio) - must be before /campaigns/:id */}
      <Route
        path="/campaigns/:id/ad_units/:adUnitId"
        element={
          <ProtectedRoute>
            <Campaigns />
          </ProtectedRoute>
        }
      />
      {/* Campaign detail - existing page */}
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <Campaigns />
          </ProtectedRoute>
        }
      />
      {/* Brand Kits list */}
      <Route
        path="/brand-kits"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/load"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:uid"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      {/* Campaign dashboard */}
      <Route
        path="/dashboard/campaign/:id"
        element={
          <ProtectedRoute>
            <CampaignDashboardPage />
          </ProtectedRoute>
        }
      />
      {/* Ad unit dashboard */}
      <Route
        path="/dashboard/campaign/:campaignId/ad_unit/:adUnitId"
        element={
          <ProtectedRoute>
            <AdUnitDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <AuthProvider>
      <Helmet>
        <title>AgenX</title>
        <meta name="description" content="AI-powered campaign strategy generation with real-time optimization" />
      </Helmet>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App
